/**
 * Product telemetry shared by every client: Google Analytics 4 events (web/desktop through firebase/analytics, iOS and
 * Android through Firebase Analytics), Crashlytics / GA4 `exception` error reports and Microsoft Clarity replays.
 * Everything here runs only under the analytics consent rule (logic/analyticsConsent), except native crash reports.
 *
 * Privacy (the reason this module exists): nothing that leaves the device names a person or carries content.
 * - Events are the fixed names below, with coarse enum parameters (sign-in method, channel kind, import source).
 * - The user id the vendors see is opaque: the first 32 hex chars of SHA-256("bundle.telemetry.v1:" + userId)
 *   (`telemetryIdSource` → the platform's SHA-256 → `telemetryIdFromDigest`). Never the raw id, a name or an email.
 * - Error text is scrubbed (`scrubErrorText`) before it becomes a GA4 `exception` description or a Crashlytics
 *   non-fatal's message: emails, URLs, quoted text and id-like tokens are replaced, and it is capped at 100 chars
 *   (GA4's limit for a parameter value).
 */

/** Every GA4 event Bundle sends (recommended names where GA4 has one: sign_up, login). Natives mirror this list. */
export const TELEMETRY_EVENTS = [
  "sign_up",
  "login",
  "workspace_created",
  "message_sent",
  "huddle_started",
  "import_started",
  "import_completed",
  "mail_sent",
] as const;
export type TelemetryEventName = (typeof TELEMETRY_EVENTS)[number];

/** The event catalog (a function so the golden vectors pin it and the native enums are checked against it). */
export function telemetryEventNames(): string[] {
  return [...TELEMETRY_EVENTS];
}

/** Versioned so the opaque id can be rotated by changing the prefix. */
export const TELEMETRY_ID_PREFIX = "bundle.telemetry.v1:";

/** The string whose SHA-256 becomes the opaque telemetry user id. */
export function telemetryIdSource(userId: string): string {
  return TELEMETRY_ID_PREFIX + userId;
}

/** SHA-256 hex digest (any case) → the opaque id: its first 32 hex chars, lower-case. */
export function telemetryIdFromDigest(sha256Hex: string): string {
  return sha256Hex.trim().toLowerCase().slice(0, 32);
}

/** GA4 caps event parameter values at 100 characters. */
export const ERROR_TEXT_MAX = 100;

const URL_RE = /[A-Za-z][A-Za-z0-9+.-]*:\/\/[^\s"'`]+/g;
const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
/** Quoted text ("…", '…', `…`, “…”, ‘…’); a single quote only opens after a non-word char (so "can't" stays). */
const QUOTED_RE = /"[^"]*"|(^|[^A-Za-z0-9])'[^']*'|`[^`]*`|“[^”]*”|‘[^’]*’/g;
const TOKEN_RE = /[A-Za-z0-9_-]+/g;

/**
 * Error text safe to send: emails → `<email>`, URLs → `<url>`, quoted text → `<text>`, tokens of 6+ chars
 * ([A-Za-z0-9_-]) that contain a digit (ids, ULIDs, UUID parts, long numbers) → `<id>`; whitespace collapsed; at
 * most 100 UTF-16 code units (a trailing lone high surrogate is dropped); empty → "error".
 */
export function scrubErrorText(text: string): string {
  let out = text
    .replace(URL_RE, "<url>")
    .replace(EMAIL_RE, "<email>")
    .replace(QUOTED_RE, (_m, pre: string | undefined) => `${pre ?? ""}<text>`)
    .replace(TOKEN_RE, (t) => (t.length >= 6 && /[0-9]/.test(t) ? "<id>" : t))
    .replace(/\s+/g, " ")
    .trim();
  if (out.length > ERROR_TEXT_MAX) {
    out = out.slice(0, ERROR_TEXT_MAX);
    const last = out.charCodeAt(out.length - 1);
    if (last >= 0xd800 && last <= 0xdbff) out = out.slice(0, -1);
    out = out.trimEnd();
  }
  return out === "" ? "error" : out;
}
